import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import AuthShell from "@/components/AuthShell";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success("Welcome back.");
      const to = location.state?.from || "/pricing";
      navigate(to, { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Sign in failed.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      subtitle="Member access"
      title="Sign in to view membership options."
      footerText="No account yet?"
      footerHref="/register"
      footerLinkLabel="Register"
    >
      <form data-testid="login-form" onSubmit={onSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-xs text-[#6B6B6B]">Email</Label>
          <Input
            id="email"
            type="email"
            data-testid="login-email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@studio.com"
            required
            className="border-black/10 bg-[#F7F5F2] text-[#2A2724] placeholder:text-[#9a9a9a] focus-visible:ring-gold"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="text-xs text-[#6B6B6B]">Password</Label>
          <Input
            id="password"
            type="password"
            data-testid="login-password"
            value={form.password}
            onChange={(e) => update("password", e.target.value)}
            placeholder="Your password"
            required
            className="border-black/10 bg-[#F7F5F2] text-[#2A2724] placeholder:text-[#9a9a9a] focus-visible:ring-gold"
          />
        </div>

        {error && (
          <p data-testid="login-error" className="text-sm text-red-600">
            {error}
          </p>
        )}

        <Button
          type="submit"
          data-testid="login-submit"
          disabled={loading}
          className="w-full rounded-full bg-gold py-6 text-sm font-medium text-black hover:bg-[#AA8627]"
        >
          {loading ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </AuthShell>
  );
}
